import { isBoolean, merge } from 'es-toolkit';
import EventEmitter from 'eventemitter3';
import { COMPANION_AD_SIZES, } from '../AdPlayer';
import { ADS_VIDEO_VPAID_MODE, PARTNER_ADS_CONFIG } from '../constants';
import { api } from '../singletons';
class AppSessionDataProvider extends EventEmitter {
    #data = { breadcrumbId: '' };
    #readyPromise = Promise.withResolvers();
    #buildAdOptions(adConfig, isOutstream = false) {
        const config = api.getConfigSetting(PARTNER_ADS_CONFIG);
        const providers = config ? this.#deriveProviders(config) : undefined;
        return {
            providers,
            providerOptions: {
                ...adConfig,
                ...(this.consentData?.isTargetingEnabled && {
                    ppid: api.adsTargeting?.ppid,
                }),
                ...(isOutstream && {
                    allowContentlessVmap: true,
                }),
                vpaidMode: api.getConfigSetting(ADS_VIDEO_VPAID_MODE, 'disabled'),
                companionAdSizes: COMPANION_AD_SIZES,
                // TODO: WMP's default ad loader timeout is good enough for now, but we can create a setting later if we need to
                // adLoaderTimeout: api.getConfigSetting<number>(AD_LOADER_TIMEOUT),
            },
            privacyData: this.consentData,
        };
    }
    get ready() {
        return this.#readyPromise.promise;
    }
    get isInDiscordIframe() {
        return this.#data.isInDiscordIframe || false;
    }
    get hasAdConfig() {
        const config = api.getConfigSetting(PARTNER_ADS_CONFIG);
        if (!config) {
            return false;
        }
        const providers = this.#deriveProviders(config);
        return providers.length > 0;
    }
    #deriveProviders(config) {
        // Legacy format has an explicit providers map
        if ('providers' in config) {
            const legacyProviders = config.providers;
            return Object.keys(legacyProviders);
        }
        return Object.keys(config);
    }
    get adOptions() {
        return this.#buildAdOptions(this.#data.adConfig);
    }
    get outstreamVideoAdOptions() {
        return this.#buildAdOptions(this.#data.outstreamVideoAdConfig, true);
    }
    get consentData() {
        return this.#data.consentData || {};
    }
    get streamsConsentParams() {
        const { audiences, usPrivacyString, gdprTcString, gdprApplies, optedInGeneralVendorIds } = this.consentData;
        const params = {
            us_privacy: usPrivacyString,
            audience: audiences ? `;${audiences.join(';')};` : undefined,
        };
        // gdprApplies is undefined until OneTrust determines whether GDPR applies
        // IMPORTANT: GDPR ATTRIBUTE MUST BE STRING
        if (isBoolean(gdprApplies)) {
            params.gdpr = gdprApplies ? '1' : '0';
        }
        if (gdprTcString) {
            params.gdpr_consent = gdprTcString;
        }
        if (optedInGeneralVendorIds) {
            params.genVendors = optedInGeneralVendorIds;
        }
        return params;
    }
    get breadcrumbId() {
        return this.#data.breadcrumbId || '';
    }
    get env() {
        return this.#data.env || 'prod';
    }
    get currentScreen() {
        return this.#data.currentScreen || '';
    }
    get categoryId() {
        return this.#data.categoryId || '';
    }
    get firstVisitTimestampMs() {
        return this.#data.firstVisitTimestampMs || 0;
    }
    async init(data) {
        await this.update(data);
        this.#readyPromise.resolve();
    }
    async update(data) {
        if (!data) {
            return;
        }
        this.#data = merge(this.#data, data);
        if (data.consentData) {
            this.emit('consentDataUpdated', this.#data.consentData);
        }
    }
}
export const appSessionDataProvider = new AppSessionDataProvider();
